"use client"

import {
  Paper,
  Badge,
  Group,
  Text,
  Button,
  Table,
  ScrollArea,
  Tooltip,
  Collapsible,
  TbEye,
  CountryFlag,
  TbHomeEco,
} from "@forestsync/ui";
import { useTranslations } from "next-intl";
import type { User } from "@/types/glassroom";
import { getCountryCode, getCountryName } from "@forestsync/db";
import Link from "next/link";
import { getOrganizationTypeBadge } from "@forestsync/lib/type-utils";
import { USER_ROLES } from "@forestsync/db";

interface BeneficiaryTableProps {
  beneficiaries: User[];
}

export function BeneficiaryTable({ beneficiaries }: BeneficiaryTableProps) {
  const t = useTranslations();

  const groupedByCountry = beneficiaries.reduce(
    (groups, ben) => {
      // Normalize the key so mixed-format DB values (e.g. "SE" vs "Sweden")
      // group under a single country node instead of splitting the tree.
      const country = getCountryName(getCountryCode(ben.country));
      if (!groups[country]) groups[country] = [];
      groups[country].push(ben);
      return groups;
    },
    {} as Record<string, User[]>,
  );

  const formatDate = (date?: string | Date) => {
    if (!date) return t("common.noData");
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(dateObj);
  };

  if (beneficiaries.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        <Text className="text-center text-gray-500 dark:text-gray-400">
          {t("glassroom.noBeneficiariesFound")}
        </Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder p={{ base: "xs", sm: "md" }} radius="md">
      <div className="space-y-2">
        {Object.entries(groupedByCountry)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([country, bens]) => (
            <Collapsible
              key={country}
              label={getCountryName(getCountryCode(country))}
              count={bens.length}
              icon={<CountryFlag countryCode={getCountryCode(country)} />}
              depth={0}
              color="blue"
            >
              <ScrollArea ml={{ base: "0.5rem", sm: "1rem" }}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("common.name")}</Table.Th>
                      <Table.Th>
                        {t("glassroom.table.organizationType")}
                      </Table.Th>
                      <Table.Th>
                        {t("glassroom.table.creditsPurchased")}
                      </Table.Th>
                      <Table.Th>{t("glassroom.table.creditsRetired")}</Table.Th>
                      <Table.Th>{t("glassroom.table.lastPurchased")}</Table.Th>
                      <Table.Th>{t("glassroom.table.contactPerson")}</Table.Th>
                      <Table.Th>{t("common.actions")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {bens.map((ben) => {
                      const orgType = getOrganizationTypeBadge(ben.type, t);
                      return (
                        <Table.Tr key={ben.refId}>
                          <Table.Td>
                            <div>
                              <Group gap="xs" align="center">
                                <div>
                                  <Text fw={500} size="sm">
                                    {ben.name}
                                  </Text>
                                  <Text
                                    size="xs"
                                    className="text-gray-500 font-mono"
                                  >
                                    {ben.refId}
                                  </Text>
                                </div>
                                {ben.role === USER_ROLES.BOTH && (
                                  <Tooltip
                                    label={t("glassroom.alsoProjectDeveloper")}
                                  >
                                    <Badge
                                      color="timber"
                                      size="xs"
                                      leftSection={<TbHomeEco size={10} />}
                                    >
                                      {t("common.pd")}
                                    </Badge>
                                  </Tooltip>
                                )}
                              </Group>
                            </div>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={orgType.color}
                              variant="light"
                              size="sm"
                            >
                              {orgType.label}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {ben.creditsPurchased.toLocaleString()}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {ben.creditsRetired.toLocaleString()}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              className="text-gray-600 dark:text-gray-400"
                            >
                              {formatDate(ben.lastPurchaseDate)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              className="text-gray-600 dark:text-gray-400"
                            >
                              {ben.contactInfo?.name || t("common.noData")}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Button
                              variant="light"
                              size="xs"
                              leftSection={<TbEye size={14} />}
                              component={Link}
                              href={`/beneficiary/${ben.refId}`}
                            >
                              {t("common.view")}
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Collapsible>
          ))}
      </div>
    </Paper>
  );
}
